import { Box, VStack, Link, Text, Icon, Flex, Button, Image, Collapse, useDisclosure, HStack } from '@chakra-ui/react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { FiHome, FiUsers, FiSettings, FiGrid, FiMic, FiLogOut, FiCheckCircle, FiCalendar, FiActivity, FiCoffee, FiChevronDown, FiChevronUp, FiCreditCard, FiDownload, FiBell, FiMessageSquare, FiSend } from 'react-icons/fi';
import PropTypes from 'prop-types';
import { useAuth } from '../context/AuthContext';

const LinkItem = ({ icon, children, to, isSubItem = false }) => {
    return (
        <Link
            as={NavLink}
            to={to}
            style={{ textDecoration: 'none' }}
            _activeLink={{
                bg: 'brand.50',
                color: 'brand.600',
                fontWeight: 'bold',
                borderRight: isSubItem ? 'none' : '3px solid',
                borderColor: 'brand.500',
                bgGradient: isSubItem ? 'linear(to-r, brand.50, transparent)' : 'none'
            }}
            w="100%"
            p={isSubItem ? 2 : 3}
            pl={isSubItem ? 8 : 3}
            borderRadius="lg"
            display="flex"
            alignItems="center"
            _hover={{ bg: 'neutral.100' }}
            color="neutral.600"
            fontSize={isSubItem ? "sm" : "md"}
        >
            <Icon as={icon} mr={3} fontSize={isSubItem ? "sm" : "md"} />
            <Text>{children}</Text>
        </Link>
    );
};

LinkItem.propTypes = {
    icon: PropTypes.elementType.isRequired,
    children: PropTypes.node.isRequired,
    to: PropTypes.string.isRequired,
    isSubItem: PropTypes.bool
};

const NavGroup = ({ title, icon, children, defaultIsOpen = false, activePaths = [] }) => {
    const { isOpen, onToggle } = useDisclosure({ defaultIsOpen });
    const location = useLocation();

    // Auto-expand if a child path is active
    const isChildActive = activePaths.some(path => location.pathname === path);
    const effectiveIsOpen = isOpen || isChildActive;

    return (
        <Box w="100%">
            <Flex
                p={3}
                alignItems="center"
                cursor="pointer"
                onClick={onToggle}
                borderRadius="lg"
                _hover={{ bg: 'neutral.50' }}
                color="neutral.500"
                transition="all 0.2s"
            >
                <Icon as={icon} mr={3} />
                <Text fontWeight="semibold" fontSize="sm" flex="1">{title}</Text>
                <Icon as={effectiveIsOpen ? FiChevronUp : FiChevronDown} />
            </Flex>
            <Collapse in={effectiveIsOpen} animateOpacity>
                <VStack spacing={1} align="stretch" mt={1}>
                    {children}
                </VStack>
            </Collapse>
        </Box>
    );
};

const Sidebar = () => {
    const { user, logout } = useAuth();
    const role = user?.role;
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // 1. 定義置頂項目 (不放入分類)
    const pinnedItems = [
        { name: '儀表板', icon: FiHome, to: '/dashboard', roles: ['super_admin', 'admin', 'teacher', 'receptionist'] },
        { name: '機構狀況', icon: FiActivity, to: '/institution-status', roles: ['super_admin', 'admin'] },
        { name: '公告管理', icon: FiBell, to: '/announcements', roles: ['super_admin', 'admin', 'receptionist', 'teacher'] },
    ];

    // 2. 定義功能分組
    const groups = [
        {
            title: '電子聯絡簿',
            icon: FiMic,
            items: [
                { name: '電子聯絡簿', icon: FiMic, to: '/teacher/comm-book', roles: ['teacher', 'admin', 'super_admin', 'receptionist'] },
                // 「聯絡簿紀錄」(/teacher/history-comm-book) 的入口換成親師對話。
                // 舊頁與路由都還在（App.jsx 未移除），只是不再從側欄進入——同一套聯絡簿內容
                // 在對話裡本來就會以 comm_book 卡片呈現，兩個入口並存反而讓人不知道該點哪個。
                { name: '親師對話', icon: FiMessageSquare, to: '/teacher/chat', roles: ['teacher', 'admin', 'super_admin', 'receptionist'] },
                { name: '整班群發', icon: FiSend, to: '/teacher/broadcast', roles: ['teacher', 'admin', 'super_admin', 'receptionist'] },
            ]
        },
        {
            title: '點名與排課',
            icon: FiCheckCircle,
            items: [
                { name: '班級點名', icon: FiCheckCircle, to: '/roll-call', roles: ['teacher', 'receptionist', 'admin', 'super_admin'] },
                { name: '每日點名狀況', icon: FiCheckCircle, to: '/daily-attendance', roles: ['super_admin', 'admin', 'receptionist'] },
                { name: '英文補課', icon: FiCalendar, to: '/makeup', roles: ['teacher', 'receptionist', 'admin', 'super_admin'] },
            ]
        },
        {
            title: '行政與接送',
            icon: FiGrid,
            items: [
                { name: '簽到系統', icon: FiCheckCircle, to: '/frontdesk', roles: ['receptionist', 'admin', 'super_admin'] },
                { name: '接送控制台', icon: FiGrid, to: '/pickup-monitor', roles: ['receptionist', 'admin', 'super_admin'] },
                { name: '請假管理', icon: FiCalendar, to: '/leaves', roles: ['admin', 'super_admin', 'receptionist', 'teacher'] },
                { name: '訂餐管理', icon: FiCoffee, to: '/lunch', roles: ['admin', 'super_admin', 'receptionist', 'teacher'] },
                { name: '報表輸出', icon: FiDownload, to: '/reports', roles: ['admin', 'super_admin', 'receptionist', 'teacher'] },
            ]
        },
        {
            title: '資料管理',
            icon: FiUsers,
            items: [
                { name: '學生管理', icon: FiUsers, to: '/students', roles: ['admin', 'super_admin', 'receptionist', 'teacher'] },
                { name: '舊生查詢', icon: FiUsers, to: '/archived-students', roles: ['admin', 'super_admin', 'receptionist'] },
                { name: '班級管理', icon: FiUsers, to: '/classes', roles: ['admin', 'super_admin', 'teacher'] },
                // 人員管理（super_admin / admin 共用同一組分頁入口）
                ...(['super_admin', 'admin'].includes(role) ? [
                    { name: '教職員管理', icon: FiUsers, to: '/staff' },
                    { name: '家長管理', icon: FiUsers, to: '/parents' }
                ] : []),
            ]
        },
        {
            title: '系統設定',
            icon: FiSettings,
            items: [
                { name: '機構管理', icon: FiSettings, to: '/institutions', roles: ['super_admin'] },
                // receptionist 原本漏在這個清單外，導致行政老師在桌面端沒有任何入口進得到
                // /settings 改自己的密碼（手機 PWA 的 MobileSettings 一直都給全體教職員）。
                // 這一頁對非 admin 只顯示個人資料區塊（機構設定區塊在 SettingsPage 內另有
                // admin/super_admin 條件），補進來不會讓行政老師看到機構設定。
                { name: '系統設定', icon: FiSettings, to: '/settings', roles: ['super_admin', 'admin', 'teacher', 'receptionist'] },
            ]
        }
    ];

    return (
        <Box w="250px" h="100%" bg="white" borderRight="1px solid" borderColor="neutral.200" py={5} px={3} display="flex" flexDirection="column">
            <Flex align="center" mb={6} px={3} flexShrink={0}>
                <Image
                    src="/logo-transparent.png"
                    alt="Logo"
                    boxSize="42px"
                    mr={3}
                    objectFit="contain"
                />
                <Text fontSize="xl" fontWeight="bold" color="brand.900" lg:noOfLines={1}>
                    天籟徐薇補習班
                </Text>
            </Flex>

            <VStack
                spacing={2}
                align="stretch"
                flex="1"
                overflowY="auto"
                css={{
                    '&::-webkit-scrollbar': { width: '4px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { background: '#CBD5E0', borderRadius: '4px' },
                }}
            >
                {/* 1. 渲染置頂項目 */}
                {pinnedItems
                    .filter(item => !item.roles || item.roles.includes(role))
                    .map((item) => (
                        <LinkItem key={item.to} to={item.to} icon={item.icon}>
                            {item.name}
                        </LinkItem>
                    ))
                }

                <Box h={2} /> {/* 間距 */}

                {/* 2. 渲染分類分組 */}
                {groups.map((group) => {
                    // Filter items in group based on roles
                    const visibleItems = group.items.filter(item => !item.roles || item.roles.includes(role));
                    if (visibleItems.length === 0) return null;

                    return (
                        <NavGroup
                            key={group.title}
                            title={group.title}
                            icon={group.icon}
                            activePaths={visibleItems.map(i => i.to)}
                        >
                            {visibleItems.map(item => (
                                <LinkItem key={item.to} to={item.to} icon={item.icon} isSubItem={true}>
                                    {item.name}
                                </LinkItem>
                            ))}
                        </NavGroup>
                    );
                })}
            </VStack>


            <Box borderTop="1px solid" borderColor="neutral.200" pt={4} mt={4} flexShrink={0}>
                <Button
                    variant="ghost"
                    w="full"
                    justifyContent="flex-start"
                    leftIcon={<Icon as={FiLogOut} />}
                    colorScheme="red"
                    onClick={handleLogout}
                >
                    登出系統
                </Button>
            </Box>
        </Box>
    );
};

export default Sidebar;
